import { getActivePinKey, getDeviceKey } from '../state/security/vault.js';

type EncryptedPayload = { iv: number[]; ct: number[] };

async function encrypt(cryptoKey: CryptoKey, plaintext: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder();
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    enc.encode(plaintext),
  );
  const payload: EncryptedPayload = {
    iv: Array.from(iv),
    ct: Array.from(new Uint8Array(ciphertext)),
  };
  return JSON.stringify(payload);
}

async function decrypt(cryptoKey: CryptoKey, serialized: string): Promise<string> {
  const { iv, ct } = JSON.parse(serialized) as EncryptedPayload;
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(iv) },
    cryptoKey,
    new Uint8Array(ct),
  );
  return new TextDecoder().decode(decrypted);
}

export const secureStorage = {
  async getItem(key: string): Promise<string | null> {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        'iv' in parsed &&
        'ct' in parsed
      ) {
        const cryptoKey = getActivePinKey() ?? getDeviceKey();
        if (!cryptoKey) return null;
        return await decrypt(cryptoKey, raw);
      }
      return raw;
    } catch {
      return raw;
    }
  },

  async setItem(key: string, value: string): Promise<void> {
    const cryptoKey = getActivePinKey() ?? getDeviceKey();
    if (!cryptoKey) {
      localStorage.setItem(key, value);
      return;
    }
    const encrypted = await encrypt(cryptoKey, value);
    localStorage.setItem(key, encrypted);
  },

  removeItem(key: string): void {
    localStorage.removeItem(key);
  },

  clear(): void {
    localStorage.clear();
  },
};
